/**
 * 将全角字符（日文/中文输入法常见）自动转换为对应的半角字符。
 * 转换范围：
 *   - 全角数字（０-９ → 0-9）
 *   - 全角英文字母（Ａ-Ｚ / ａ-ｚ → A-Z / a-z）
 *   - 常见全角标点（！→! 。不在此范围，汉字不受影响）
 * 中文汉字、片假名等字符不在 FF01-FF5E 范围内，不会被误转。
 * @param {string} str 输入字符串
 * @returns {string} 转换后的字符串；若输入为 null/undefined/空则原样返回
 */
export function fullWidthToHalfWidth(str) {
    if (!str) return str;
    return str
        // Unicode 全角可打印字符区间 FF01-FF5E 与 ASCII 0x21-0x7E 一一对应，差值固定为 0xFEE0
        .replace(/[\uFF01-\uFF5E]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
        // 全角空格（U+3000）转普通空格
        .replace(/\u3000/g, ' ');
}

/**
 * 用于原生 input/compositionend 监听器的全角→半角拦截处理器。
 *
 * 【为何不能用 beforeinput + preventDefault】
 * W3C Input Events 规范中，IME composition 提交时的 beforeinput 事件
 * （inputType=insertCompositionText / insertFromComposition）在主流浏览器中
 * 不可取消，preventDefault() 无效，全角字符仍会被写入 DOM。
 *
 * 【为何不能用 normalize / onChange 转换】
 * IME 提交后 React onChange 拿到全角字符，normalize 转换为半角写入 form store，
 * Form.Item 受控重渲染把半角字符同步回 DOM，但此时 IME 仍在追加原始全角字符，
 * 最终 DOM 变成 "半角+全角"，再次 onChange → normalize → "半角+半角"，
 * 即一个全角冒号变成两个半角冒号的双字符 bug。
 *
 * 【正确方案】
 * - 监听原生 input 事件：非 IME 输入（粘贴、直接按键）立即转换
 * - 监听原生 compositionend 事件：等 IME 提交完成（setTimeout 0）后再转换
 * - 通过 native setter 写值并 dispatch input 事件，触发 React 受控更新
 *
 * 使用方式（组件内）：
 *   const inputRef = useRef(null);
 *   useEffect(() => attachHalfWidthAutoConvert(inputRef.current?.input), []);
 *   <Input ref={inputRef} />
 *
 * @param {HTMLInputElement|HTMLTextAreaElement|null|undefined} el 目标元素
 * @returns {()=>void} 解绑函数，可直接用于 useEffect 返回
 */
export function attachHalfWidthAutoConvert(el) {
    if (!el) return () => {};

    const nativeSetter = Object.getOwnPropertyDescriptor(
        el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
        'value'
    ).set;

    // 转换当前 value 并触发受控更新；保护光标位置在末尾追加场景下不跳到开头
    const convert = () => {
        const cur = el.value;
        const next = fullWidthToHalfWidth(cur);
        if (next === cur) return;
        const selStart = el.selectionStart;
        const selEnd = el.selectionEnd;
        // 字符差异：全角字符与半角字符长度都为 1，无需偏移修正
        nativeSetter.call(el, next);
        // 还原光标位置（避免 React 受控渲染后光标跳到末尾）
        try {
            el.setSelectionRange(selStart, selEnd);
        } catch (_) {
            // 某些 input type 不支持 setSelectionRange，忽略
        }
        // 派发 input 事件，触发 React 合成 onChange / Form.Item 数据同步
        el.dispatchEvent(new Event('input', { bubbles: true }));
    };

    // 原生 input 事件：跳过 IME 合成中间状态（e.isComposing），非合成情况立即转换
    const onInput = e => {
        if (e.isComposing) return;
        convert();
    };

    // IME 提交完成：用 setTimeout(0) 等浏览器把合成字符全部写入 DOM 之后再转换
    const onCompositionEnd = () => {
        setTimeout(convert, 0);
    };

    el.addEventListener('input', onInput);
    el.addEventListener('compositionend', onCompositionEnd);

    return () => {
        el.removeEventListener('input', onInput);
        el.removeEventListener('compositionend', onCompositionEnd);
    };
}
